import './webpack_public_path'; // Needs to be top import!
import { loadableReady } from '@loadable/component';
import React from 'react';
import { CookiesProvider } from 'react-cookie';
import ReactDom from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { isDesktop } from '@westwing/ui-kit/breakPoints';
import { HelmetProvider } from 'react-helmet-async';
import { Config } from '../common/types/config';
import { FeatureToggles } from '../common/types/featureToggles';
import { createBrazeEventsSimulator } from '../common/events/brazeSimulator';
import EventListener from '../common/events';
import { InitSSR } from './components/App';
import theme from './components/GlobalStyles/theme';
import RouteRedirect from './components/Redirects/RouteRedirects';
import getConfig from './config';
import GaTracking from './ga';
import { DataLayerManagerInterface } from './ga/dataLayerManager/abstractDataLayerManager';
import { DataLayerManagerType } from './ga/dataLayerManager/dataLayerManagerFactory';
import dataLayerStoreConnector from './ga/dataLayerManager/dataLayerStoreConnector';
import { DataLayerPlatform } from './ga/dataLayerManager/serverDataLayerManager';
import BrazeClient, { createBrazeClientConfig } from './hooks/Braze/BrazeClient';
import { connectToStore } from './hooks/Braze/brazeStoreConnector';
import { BrazeProvider } from './hooks/Braze/useBraze';
import ClientFetchActionManager from './hooks/useFetchingAction/ClientFetchActionManager';
import { FetchManagerProvider } from './hooks/useFetchingAction/FetchManagerContext';
import { GaTrackingProvider } from './hooks/useGa';
import { LoggerProvider } from './logger/LoggerProvider';
import { ConfigProvider, FeatureToggleProvider, TranslationProvider } from './providers';
import { brandActions } from './redux/brands/actions';
import { fetchCartCount } from './redux/cart/actions';
import { categoryActions } from './redux/category/actions';
import configureStore, { createDependencies } from './redux/store';
import { getLoggedInUserData } from './redux/user/actions';
import ServiceWorkerManager from './serviceWorker/serviceWorkerManager';
import { SWManagerProvider } from './serviceWorker/swManagerProvider';
import {
    oneTrustConsentEventDataParser,
    ONETRUST_CONSENT_READY_EVENT_NAME,
    OnetrustActiveGroups,
} from './WestwingEventHandler';
import publishEvent from './redux/eventsQueue/actions';
import StateInterface from './redux/types';

// Grab the state from a global variable injected into the server-generated HTML
const initialStore = window.initialStore || {};
const translations = window.translations || ({} as any);
const config: Config = getConfig();
const featureToggles: FeatureToggles = window.featureToggles || {};
const { language, country, clientLogger } = window;
const { origin } = window.location;

// Allow the passed state to be garbage-collected
delete window.initialStore;
delete window.language;
delete window.country;
delete window.translations;
delete window.clientLogger;

const swManager = new ServiceWorkerManager(clientLogger, config);

const gaTracking = new GaTracking(
    DataLayerManagerType.CLIENT,
    {
        currency: config.currency,
        platform: DataLayerPlatform.NONE,
    },
    clientLogger
);

// Reset the bella base url to the current url
config.bellaApiBaseUrl = window.origin;
// get current user agent
const { userAgent } = navigator;
// Create Redux store with initial state
const store = configureStore(
    initialStore,
    createDependencies(config.shopApiBaseUrl, config, clientLogger, country, userAgent, window.location, gaTracking)
);
// Exposes a copy of the state globally - mostly useful for UATs
window.getState = (): StateInterface => ({ ...store.getState() });

window.onpageshow = () => {
    // Initial client-side state should be handled here

    // It is fixing browser back navigation issue
    // when the page state is inconsistent
    // This handler should be triggered every time the page is shown, either first load or from cache
    // https://developer.mozilla.org/en-US/docs/Mozilla/Firefox/Releases/1.5/Using_Firefox_1.5_caching
    // https://developer.mozilla.org/en-US/docs/Web/API/Window/pageshow_event
    store.dispatch(getLoggedInUserData());
    store.dispatch(fetchCartCount());
    store.dispatch(categoryActions.thunk());
    store.dispatch(brandActions.thunk());
};

window.onload = swManager.bootstrap.bind(swManager);

const fetchingManager = new ClientFetchActionManager();
const brazeConfig = store.getState().configurations.braze;
const brazeClientConfig = createBrazeClientConfig(brazeConfig, config, isDesktop());
const braze = new BrazeClient(clientLogger, brazeClientConfig);

// listen to document events to trigger braze rate your product for testing
// this is achieved by execute the following on a browser console
// document.dispatchEvent(new CustomEvent('SIMULATE_BRAZE_PRODUCT_RATINGS'));
const brazeSimulatorEvents = createBrazeEventsSimulator(braze);
EventListener(brazeSimulatorEvents);

window.addEventListener('load', () => {
    try {
        if (config.env === 'local') {
            connectToStore(braze, store);
        }
        // Listen to an event triggered by GTM and load Braze only if we have consent from One Trust Cookie overlay.
        window.ww.events.subscribe(ONETRUST_CONSENT_READY_EVENT_NAME, (data: OnetrustActiveGroups) => {
            // @Todo validate the payload and only unsubscribe() if we have found a valid payload.
            const parsedGroups = oneTrustConsentEventDataParser(data);
            store.dispatch(publishEvent({ id: ONETRUST_CONSENT_READY_EVENT_NAME, data: parsedGroups }));
            if (parsedGroups.targetingCookies) {
                clientLogger.debug('Received consent for targeting cookies, connecting Braze');
                connectToStore(braze, store);
            }
        });
    } catch (e) {
        clientLogger.error(`Unable to subscribe to ${ONETRUST_CONSENT_READY_EVENT_NAME} event`, { message: e.message });
    }
});

dataLayerStoreConnector.connect(gaTracking.getDataLayerManager() as DataLayerManagerInterface, store);

loadableReady(() => {
    ReactDom.hydrate(
        <HelmetProvider>
            <FetchManagerProvider value={fetchingManager}>
                <SWManagerProvider value={swManager}>
                    <LoggerProvider value={clientLogger}>
                        <ThemeProvider theme={theme}>
                            <TranslationProvider translations={translations} language={language}>
                                <CookiesProvider>
                                    <FeatureToggleProvider value={featureToggles}>
                                        <BrazeProvider value={braze}>
                                            <ConfigProvider initialConfig={config}>
                                                <Provider store={store}>
                                                    <GaTrackingProvider value={gaTracking}>
                                                        <Router>
                                                            <RouteRedirect />
                                                            <InitSSR host={origin} />
                                                        </Router>
                                                    </GaTrackingProvider>
                                                </Provider>
                                            </ConfigProvider>
                                        </BrazeProvider>
                                    </FeatureToggleProvider>
                                </CookiesProvider>
                            </TranslationProvider>
                        </ThemeProvider>
                    </LoggerProvider>
                </SWManagerProvider>
            </FetchManagerProvider>
        </HelmetProvider>,
        document.querySelector('#app-root')
    );
});

if (module.hot) {
    module.hot.accept();
}
